// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  InputRadioGroup,
  LgPadding,
  LgSpacing,
  ListButton,
  Row,
} from '@alto/design-system';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { PaymentMethodRadioOption } from '~web/features/checkout/components/PaymentMethodRadioOption';
import { Button } from '~web/features/essentials/primitives';
import { CreditCardActionSheet } from '~web/features/payments/components/CreditCardActionSheet';

type Props = {
  readonly onSubmit: (paymentMethodID?: number) => void;
};
export const SelectPaymentMethodActionSheet = ({ onSubmit }: Props) => {
  const { trackEvent } = useAnalytics();
  const { closeActionSheet, setActiveActionSheet, goToPreviousActionSheet } = useContext(ActionSheetContext);
  const [paymentMethodID, setPaymentMethodID] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const { paymentMethods } = useGetPaymentMethods();
  const selectedMethod = paymentMethods.find((paymentMethod) => paymentMethod.id === paymentMethodID);

  const handleSelectPaymentMethod = useCallback(
    (paymentMethodID: string) => {
      setPaymentMethodID(parseInt(paymentMethodID, 10));
    },
    [setPaymentMethodID],
  );

  const handleAddPaymentMethodClick = useCallback(() => {
    trackEvent({ event: EVENTS.ADD_PAYMENT_METHOD_TYPE_SELECTED, params: { type: 'credit_card' } });
    setActiveActionSheet(<CreditCardActionSheet onClose={goToPreviousActionSheet} />);
  }, [goToPreviousActionSheet, setActiveActionSheet, trackEvent]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression
    await onSubmit(paymentMethodID);
    setLoading(false);
    closeActionSheet();
  }, [onSubmit, paymentMethodID, setLoading, closeActionSheet]);

  const cardText = useMemo(
    () => [selectedMethod?.details?.card_type, selectedMethod?.details?.card_last_4].filter((text) => !!text).join('-'),
    [selectedMethod?.details?.card_last_4, selectedMethod?.details?.card_type],
  );

  return (
    <ActionSheetV2 title="Select payment method">
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <InputRadioGroup onValueChange={handleSelectPaymentMethod}>
          {paymentMethods.map((paymentMethod) => (
            <PaymentMethodRadioOption
              key={paymentMethod.id}
              paymentMethod={paymentMethod}
              value={paymentMethod.id.toString()}
            />
          ))}
        </InputRadioGroup>
        <LgSpacing />
        <ListButton
          label="Add credit card"
          rightIcon="creditcard"
          onPress={handleAddPaymentMethodClick}
        />
        <LgSpacing />
        <Row center>
          <Button
            title={`Select ${cardText || 'card'}`}
            onClick={handleSubmit}
            disabled={!selectedMethod || loading}
            loading={loading}
          />
        </Row>
      </LgPadding>
    </ActionSheetV2>
  );
};
